import { useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'
import { useIntl } from 'react-intl'
import SideModalWrapper from '../../../Assets/modals/SideModalWrapper'
import { messages } from '../../messages'
import { useGetLocationsQuery } from '../../../../app/redux-fetch/apiCoreQuery'
import { TransportModeId } from '../plannerTypes'

type AddWaypointSidePanelProps = {
  open: boolean
  onClose: () => void
  onAdd: (locationId: number, locationName: string, address: string, transportModeId: number) => void
  position: 'before' | 'after'
  legIndex: number
  tenantId: number
}

const AddWaypointSidePanel = ({
  open,
  onClose,
  onAdd,
  position,
  tenantId,
}: AddWaypointSidePanelProps) => {
  const { formatMessage } = useIntl()
  const [selectedLocationId, setSelectedLocationId] = useState<number | ''>('')
  const [selectedTransportModeId, setSelectedTransportModeId] = useState<number | ''>('')

  const { data: locations = [] } = useGetLocationsQuery(
    { tenantId, includeCustomerLocations: false },
    { skip: !open || tenantId <= 0 }
  )

  const handleLocationChange = (event: SelectChangeEvent<number | ''>) => {
    setSelectedLocationId(event.target.value as number | '')
  }

  const handleTransportModeChange = (event: SelectChangeEvent<number | ''>) => {
    setSelectedTransportModeId(event.target.value as number | '')
  }

  const formatAddress = (addr: typeof locations[0]['address'] | undefined): string => {
    if (!addr) return ''
    const parts = [addr.address1, addr.address2, addr.address3, addr.city, addr.state, addr.postalCode]
      .filter(Boolean)
    return parts.join(', ')
  }

  const handleAdd = () => {
    if (selectedLocationId && selectedTransportModeId) {
      const selectedLocation = locations.find((loc) => loc.id === selectedLocationId)
      const locationName = selectedLocation?.name ?? ''
      const address = formatAddress(selectedLocation?.address)
      onAdd(selectedLocationId, locationName, address, selectedTransportModeId as number)
      handleClose()
    }
  }

  const handleClose = () => {
    setSelectedLocationId('')
    setSelectedTransportModeId('')
    onClose()
  }

  const isAddDisabled = !selectedLocationId || !selectedTransportModeId

  const headerLabel =
    position === 'before'
      ? formatMessage(messages.addWaypointBefore)
      : formatMessage(messages.addWaypointAfter)

  return (
    <SideModalWrapper
      open={open}
      onClose={handleClose}
      headerLabel={headerLabel}
      withBottomBox={false}
      width="400px"
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {formatMessage(messages.selectLocation)}
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="waypoint-location-label">
            {formatMessage(messages.location)}
          </InputLabel>
          <Select
            labelId="waypoint-location-label"
            value={selectedLocationId}
            label={formatMessage(messages.location)}
            onChange={handleLocationChange}
          >
            <MenuItem value="">
              <em>{formatMessage(messages.selectLocation)}</em>
            </MenuItem>
            {locations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {location.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="waypoint-transport-mode-label">
            {formatMessage(messages.transportModeName)}
          </InputLabel>
          <Select
            labelId="waypoint-transport-mode-label"
            value={selectedTransportModeId}
            label={formatMessage(messages.transportModeName)}
            onChange={handleTransportModeChange}
          >
            <MenuItem value="">
              <em>{formatMessage(messages.selectTransportMode)}</em>
            </MenuItem>
            <MenuItem value={TransportModeId.Road}>{formatMessage(messages.road)}</MenuItem>
            <MenuItem value={TransportModeId.Sea}>{formatMessage(messages.sea)}</MenuItem>
            <MenuItem value={TransportModeId.Air}>{formatMessage(messages.air)}</MenuItem>
            <MenuItem value={TransportModeId.Rail}>{formatMessage(messages.rail)}</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button variant="outlined" onClick={handleClose}>
            {formatMessage(messages.cancel)}
          </Button>
          <Button variant="contained" onClick={handleAdd} disabled={isAddDisabled}>
            {formatMessage(messages.add)}
          </Button>
        </Box>
      </Box>
    </SideModalWrapper>
  )
}

export default AddWaypointSidePanel
